import { Box, Button, CircularProgress, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useAuth } from '../../../../hooks/use-auth'
import { useP2PCollectionIndex } from '../../../../hooks/use-p2pCollectionIndex'
import { AwaitingInvitation } from './AwaitingInvitation'
import { CollectionStage } from './CollectionStage'
import { SubmitReportModal } from './SubmitReportModal'
import { latestUpdatedAt, shortYearLabel } from './collectionSummary'
import { type WizardStage } from './collectionStages'
import { styles } from './CollectionWizard.styles'

export type { WizardStage } from './collectionStages'

export const CollectionWizard = (): JSX.Element => {
  const { user } = useAuth()
  const { index, isLoading, reload, refreshInBackground } = useP2PCollectionIndex()
  const [stage, setStage] = useState<WizardStage>({ name: 'overview' })
  const [isSubmitOpen, setIsSubmitOpen] = useState<boolean>(false)
  const [liveSavedAt, setLiveSavedAt] = useState<string | null>(null)

  if (isLoading) return <CircularProgress data-testid='p2pWizardLoading' />

  if (index == null || (!index.isEnterable && index.awaitingInvitation)) {
    return <AwaitingInvitation yearLabel={index?.activeReportingYear?.label} />
  }

  const reportingYearId = index.activeReportingYear?.id ?? 0
  const personName = user != null ? `${user.firstName} ${user.lastName}`.trim() : ''
  const isdName = index.isdName ?? ''
  const isdLastUpdated = latestUpdatedAt(index, liveSavedAt)
  const goToOverview = (): void => { setStage({ name: 'overview' }); void reload() }

  return (
    <Box data-testid='p2pCollectionWizard' sx={styles.page}>
      <Box sx={styles.header}>
        <Box>
          <Typography variant='h1'>[{shortYearLabel(index.activeReportingYear?.label ?? '')}] Peer to Peer Data Collection</Typography>
          <Typography variant='body2' sx={styles.intro}>
            Your data helps inform local and statewide decisions about Peer to Peer programs and supports reporting to the
            Michigan Department of Education. Thank you for taking the time to contribute!
          </Typography>
        </Box>
        {stage.name !== 'overview' &&
          <Button variant='outlined' startIcon={<ArrowBackIcon />} onClick={goToOverview} data-testid='p2pBackToOverview' sx={styles.backButton}>
            Back to Overview
          </Button>}
      </Box>

      <Box sx={styles.card}>
        <Box sx={styles.identity}>
          <Typography variant='body2'><strong>{personName}</strong>{isdName !== '' ? ` | ${isdName}` : ''}</Typography>
          {isdLastUpdated != null &&
            <Typography variant='body2' sx={{ color: 'text.secondary' }} data-testid='p2pIsdLastUpdated'>{dayjs(isdLastUpdated).format('MM/DD/YYYY')}</Typography>}
        </Box>
        <CollectionStage
          stage={stage}
          onStageChange={setStage}
          index={index}
          reportingYearId={reportingYearId}
          onBackToOverview={goToOverview}
          onIndexChanged={() => { void refreshInBackground() }}
          onSaved={setLiveSavedAt}
          onSubmit={() => { setIsSubmitOpen(true) }}
        />
      </Box>

      <SubmitReportModal
        open={isSubmitOpen}
        reportingYearId={reportingYearId}
        onClose={() => { setIsSubmitOpen(false) }}
        onSubmitted={() => { setIsSubmitOpen(false); setStage({ name: 'summary' }) }}
      />
    </Box>
  )
}
